import {
  MAX_CHARS_PERSONA_DETAIL,
  MAX_CHARS_PERSON_NAMES,
  MAX_CHARS_WHERE_DID_YOU_HEAR_DETAIL,
  MIN_AGE_TO_REGISTER,
  MIN_AGE_TO_REGISTER_FAST_TRACK,
  MIN_CHARS_PERSONA_DETAIL,
  MIN_CHARS_PERSON_NAMES,
  MIN_CHARS_WHERE_DID_YOU_HEAR_DETAIL,
} from 'constants/registration'
import { initializeHeightValues } from 'utils'
import { validators } from './validators'
import { useFormLabels } from 'forms/common/translations'
import { MAX_HEIGHT_METRIC, MIN_HEIGHT_METRIC } from 'forms/common/constants'
import { FormikKeys } from 'types/forms/formik-names'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

const MIN_HEIGHT_BREAKDOWN = initializeHeightValues(MIN_HEIGHT_METRIC)
const MAX_HEIGHT_BREAKDOWN = initializeHeightValues(MAX_HEIGHT_METRIC)

export type ValidationSchema = Partial<{ [key in FormikKeys] }>

const {
  doesExist,
  isAboveMinHeight,
  isDateFormatValid,
  isDateInThePast,
  isNameValidLength,
  isDetailValidLength,
  isOfMinAge,
  isSexValid,
  isUnderMaxHeight,
  isEmailValid,
} = validators

const useParentalConsentValidation = () => {
  const { t } = useTranslation('strCommonUI')
  const labels = useFormLabels()

  return useMemo(
    () => ({
      parentGuardianFullName: [doesExist(`${labels.parentGuardianName} ${t('form.required')}`)],
      parentGuardianEmail: [
        doesExist(`${labels.parentGuardianEmail} ${t('form.required')}`),
        isEmailValid(`${labels.parentGuardianEmail} ${t('form.invalid')}`),
      ],
    }),
    [t, labels]
  )
}

const usePersonaSelectionValidation = () => {
  const { t } = useTranslation('strCommonUI')

  return useMemo(
    () => ({
      personaDetail: [
        isNameValidLength(
          `${t('form.validationErrors.minMaxChars', {
            minLength: MIN_CHARS_PERSONA_DETAIL,
            maxLength: MAX_CHARS_PERSONA_DETAIL,
          })}`
        ),
      ],
    }),
    [t]
  )
}

const useWhereDidYouHearSelectionValidation = () => {
  const { t } = useTranslation('strCommonUI')

  return useMemo(
    () => ({
      whereDidYouHearDetail: [
        isDetailValidLength(
          `${t('form.validationErrors.minMaxChars', {
            minLength: MIN_CHARS_WHERE_DID_YOU_HEAR_DETAIL,
            maxLength: MAX_CHARS_WHERE_DID_YOU_HEAR_DETAIL,
          })}`
        ),
      ],
    }),
    [t]
  )
}

const useDefaultValidationSchema = (): ValidationSchema => {
  const { t } = useTranslation('strCommonUI')
  const labels = useFormLabels()

  return useMemo(
    () => ({
      nativeGivenName: [
        doesExist(`${labels.givenName} ${t('form.required')}`),
        isNameValidLength(
          `${labels.givenName} ${t('form.validationErrors.minMaxChars', {
            minLength: MIN_CHARS_PERSON_NAMES,
            maxLength: MAX_CHARS_PERSON_NAMES,
          })}`
        ),
      ],
      nativeFamilyName: [
        doesExist(`${labels.familyName} ${t('form.required')}`),
        isNameValidLength(
          `${labels.familyName} ${t('form.validationErrors.minMaxChars', {
            minLength: MIN_CHARS_PERSON_NAMES,
            maxLength: MAX_CHARS_PERSON_NAMES,
          })}`
        ),
      ],
      birthDate: [
        doesExist({
          birthDate: `${labels.dateOfBirth} ${t('form.required')}`,
          dobDay: true,
          dobMonth: true,
          dobYear: true,
        }),
        isDateFormatValid({
          birthDate: `${labels.dateOfBirth} ${t('form.invalid')}`,
          dobDay: true,
          dobMonth: true,
          dobYear: true,
        }),
        isDateInThePast({
          birthDate: `${t('form.validationErrors.dobInThePast')}`,
          dobDay: true,
          dobMonth: true,
          dobYear: true,
        }),
      ],
      sex: [isSexValid(`${labels.sex} ${t('form.required')}`)],
      nationalityCode: [doesExist(`${labels.nationality} ${t('form.required')}`)],
      countryOfResidenceCode: [doesExist(`${labels.countryOfResidence} ${t('form.required')}`)],
      heightInMeters: [
        isAboveMinHeight(
          `${t('form.validationErrors.minHeight', {
            minHeightMetric: `${MIN_HEIGHT_BREAKDOWN.m}m ${MIN_HEIGHT_BREAKDOWN.cm}cm`,
            minHeightImperial: `${MIN_HEIGHT_BREAKDOWN.ft}ft ${MIN_HEIGHT_BREAKDOWN.inches}in`,
          })}`
        ),
        isUnderMaxHeight(
          `${t('form.validationErrors.maxHeight', {
            maxHeightMetric: `${MAX_HEIGHT_BREAKDOWN.m}m ${MAX_HEIGHT_BREAKDOWN.cm}cm`,
            maxHeightImperial: `${MAX_HEIGHT_BREAKDOWN.ft}ft ${MAX_HEIGHT_BREAKDOWN.inches}in`,
          })}`
        ),
      ],
    }),
    [t, labels]
  )
}

export const useMyDetailsValidationSchema = (): ValidationSchema => {
  const { t } = useTranslation('strCommonUI')
  const defaultValidationSchema = useDefaultValidationSchema()
  return useMemo(
    () => ({
      ...defaultValidationSchema,
      birthDate: [
        ...defaultValidationSchema.birthDate,
        isOfMinAge(
          {
            birthDate: `${t('form.validationErrors.minAgeForService', {
              minAge: MIN_AGE_TO_REGISTER,
            })}`,
            dobDay: true,
            dobMonth: true,
            dobYear: true,
          },
          MIN_AGE_TO_REGISTER
        ),
      ],
    }),
    [t, defaultValidationSchema]
  )
}

export const useActivateUserValidationSchema = (): ValidationSchema => {
  const { t } = useTranslation('strCommonUI')
  const defaultValidationSchema = useDefaultValidationSchema()
  const parentalConsentValidation = useParentalConsentValidation()
  const personaSelectionValidation = usePersonaSelectionValidation()

  return useMemo(
    () => ({
      ...defaultValidationSchema,
      ...parentalConsentValidation,
      ...personaSelectionValidation,
      birthDate: [
        ...defaultValidationSchema.birthDate,
        isOfMinAge(
          {
            birthDate: `${t('form.validationErrors.minAgeToRegister', {
              minAge: MIN_AGE_TO_REGISTER_FAST_TRACK,
            })}`,
            dobDay: true,
            dobMonth: true,
            dobYear: true,
          },
          MIN_AGE_TO_REGISTER_FAST_TRACK
        ),
      ],
    }),
    [t, defaultValidationSchema, parentalConsentValidation, personaSelectionValidation]
  )
}

export const useWalkUpRegValidationSchema = (): ValidationSchema => {
  const { t } = useTranslation('strCommonUI')
  const labels = useFormLabels()
  const defaultValidationSchema = useDefaultValidationSchema()
  const parentalConsentValidation = useParentalConsentValidation()
  const personaSelectionValidation = usePersonaSelectionValidation()
  const whereDidYouHearSelectionValidation = useWhereDidYouHearSelectionValidation()

  return useMemo(
    () => ({
      ...defaultValidationSchema,
      ...parentalConsentValidation,
      ...personaSelectionValidation,
      ...whereDidYouHearSelectionValidation,
      birthDate: [
        ...defaultValidationSchema.birthDate,
        isOfMinAge(
          {
            birthDate: `${t('form.validationErrors.minAgeToRegister', {
              minAge: MIN_AGE_TO_REGISTER,
            })}`,
            dobDay: true,
            dobMonth: true,
            dobYear: true,
          },
          MIN_AGE_TO_REGISTER
        ),
      ],
      emailAddress: [
        doesExist(`${labels.email} ${t('form.required')}`),
        isEmailValid(`${labels.email} ${t('form.invalid')}`),
      ],
    }),
    [t, labels, defaultValidationSchema, parentalConsentValidation, personaSelectionValidation]
  )
}

export const useFastTrackRegValidationSchema = (): ValidationSchema =>
  useParentalConsentValidation()
